import * as React from "react";
import { Dialog } from "./Dialog";
import { Button } from "./Button";

export interface ConfirmOptions {
    /** Headline question (e.g. "Delete this contact?"). */
    title?: React.ReactNode;
    /** Optional supporting detail under the title. */
    description?: React.ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    /** Red confirm button. Defaults to true (most confirms here are deletes). */
    destructive?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions | string) => Promise<boolean>;

const ConfirmContext = React.createContext<ConfirmFn | null>(null);

interface Pending {
    opts: ConfirmOptions;
    resolve: (value: boolean) => void;
}

/**
 * Provides an imperative, promise-based replacement for the native `confirm()`:
 *
 *   const confirm = useConfirm();
 *   if (await confirm("Delete this contact?")) remove.mutate(id);
 *
 * Renders a single themed Dialog (focus trap, Escape, backdrop). Mount once
 * near the app root. NOTE: because this shadows the global `confirm`, every
 * call site MUST `await` it — an un-awaited Promise is always truthy.
 */
export function ConfirmProvider({ children }: { children: React.ReactNode }) {
    const [pending, setPending] = React.useState<Pending | null>(null);

    const confirm = React.useCallback<ConfirmFn>((opts) => {
        const normalized: ConfirmOptions = typeof opts === "string" ? { title: opts } : opts;
        return new Promise<boolean>((resolve) => setPending({ opts: normalized, resolve }));
    }, []);

    // Resolve the outstanding promise and tear down the dialog.
    const settle = React.useCallback((result: boolean) => {
        setPending((p) => {
            p?.resolve(result);
            return null;
        });
    }, []);

    const o = pending?.opts;

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            <Dialog.Root open={pending !== null} onOpenChange={(open) => { if (!open) settle(false); }}>
                {o && (
                    <Dialog.Content className="gap-3 sm:max-w-sm">
                        {o.title && <Dialog.Title>{o.title}</Dialog.Title>}
                        {o.description && <Dialog.Description>{o.description}</Dialog.Description>}
                        <div className="mt-1 flex justify-end gap-2">
                            <Dialog.Close render={<Button>{o.cancelLabel ?? "Cancel"}</Button>} />
                            <Button
                                variant={o.destructive === false ? "primary" : "danger"}
                                onClick={() => settle(true)}
                            >
                                {o.confirmLabel ?? "Confirm"}
                            </Button>
                        </div>
                    </Dialog.Content>
                )}
            </Dialog.Root>
        </ConfirmContext.Provider>
    );
}

export function useConfirm(): ConfirmFn {
    const ctx = React.useContext(ConfirmContext);
    if (!ctx) throw new Error("useConfirm must be used within <ConfirmProvider>");
    return ctx;
}
